import { useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faCircleCheck, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateEngageMessageById } from 'api/engage';
import {
    LETTER_START_COST,
    PLUS_LETTER_START_COST,
} from 'assets/js/pages/engage/letters/constants';
import { useLetterActions } from 'assets/js/pages/engage/letters/useLetterActions';
import * as Dialog from 'components/dialog';
import { StepperContext } from 'components/stepper';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';

interface LetterEasySendNewLetterDialogProps extends Dialog.DialogProps {}

const LetterEasySendNewLetterDialog = (props: LetterEasySendNewLetterDialogProps) => {
    const { children, ...rest } = props;
    const currencySymbol = useCurrencySymbol();
    const { goForward } = useContext(StepperContext);
    const { accountId, hasGivebutterPlusAccess } = useCurrentAccount();
    const { key, forceRender } = useForceRender();
    const params = useParams<{ letter_id: string }>();
    const messageId = parseInt(params.letter_id);
    const { invalidate, createNewLetterDraftMutation } = useLetterActions();
    const { mutate: createLetterMutation, isPending } = createNewLetterDraftMutation;
    const history = useHistory();

    const { mutate: updateLetterToEasySend } = useMutation({
        mutationFn: () =>
            updateEngageMessageById(accountId, messageId, {
                settings: { send_to_print_and_mail_service: true },
            }),
        onSuccess: async () => {
            await invalidate({ type: 'all' });
            history.push(`/accounts/${accountId}/engage/letters/draft/${messageId}/edit/envelope`);
        },
    });

    const createOrUpdateManualLetter = () => {
        if (messageId) {
            forceRender();
            goForward();
        } else {
            createLetterMutation(false);
        }
    };

    const createOrUpdateEasySendLetter = () => {
        if (messageId) {
            updateLetterToEasySend();
        } else {
            createLetterMutation(true);
        }
    };

    return (
        <Dialog.Root {...rest} key={key}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content className="max-w-[68rem] bg-[url('/images/illustrations/easy_send_cta_bg.svg')] bg-right-bottom bg-no-repeat px-16 py-12">
                    <div className="relative flex flex-col gap-8">
                        <div className="flex flex-col gap-10 md:flex-row">
                            <div className="w-full rounded-xl border border-gray-200 bg-white shadow-sm md:w-1/2">
                                <div className="flex flex-col items-center justify-center gap-2 px-8 py-4 text-center text-lg font-medium">
                                    <div className="text-gray-600">Free</div>
                                    <div className="text-3xl font-medium">Self-service</div>
                                </div>
                                <Separator />
                                <div className="flex flex-col gap-10 p-8">
                                    <ul className="flex flex-col gap-4 text-lg">
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Printable PDFs
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Envelopes and labels
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            CSV of contact names and addresses
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-gray-400"
                                            />
                                            <span className="text-gray-500 line-through">
                                                Printing
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-gray-400"
                                            />
                                            <span className="text-gray-500 line-through">
                                                Postage
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-gray-400"
                                            />
                                            <span className="text-gray-500 line-through">
                                                Embeddable QR codes
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-gray-400"
                                            />
                                            <span className="text-gray-500 line-through">
                                                Individual letter customization
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faTimesCircle}
                                                className="text-gray-400"
                                            />
                                            <span className="text-gray-500 line-through">
                                                Donation conversion analytics
                                            </span>
                                        </li>
                                    </ul>
                                    <Button
                                        size="lg"
                                        variant="outline"
                                        color="neutral"
                                        disabled={isPending}
                                        onClick={() => createOrUpdateManualLetter()}
                                        className="w-full font-semibold"
                                    >
                                        Get started
                                    </Button>
                                </div>
                            </div>
                            <div className="flex w-full flex-col justify-between gap-4 rounded-xl border border-gray-200 bg-white shadow-sm md:w-1/2">
                                <div>
                                    <div className="flex flex-col items-center justify-center gap-2 px-8 py-4 text-lg font-medium">
                                        <div className="text-gray-600">
                                            {currencySymbol}
                                            {hasGivebutterPlusAccess
                                                ? PLUS_LETTER_START_COST
                                                : LETTER_START_COST}
                                            /letter
                                        </div>
                                        <div className="text-3xl font-medium">
                                            Automated mailing
                                        </div>
                                    </div>
                                    <Separator />

                                    <ul className="flex flex-col gap-4 p-8 text-lg">
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Printing
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Postage
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Embeddable QR codes
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Individual letter customization
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <FontAwesomeIcon
                                                icon={faCircleCheck}
                                                className="text-green-500"
                                            />
                                            Donation conversion analytics
                                        </li>
                                    </ul>
                                </div>
                                <div className="p-8">
                                    <Button
                                        variant="solid"
                                        size="lg"
                                        disabled={isPending}
                                        onClick={() => createOrUpdateEasySendLetter()}
                                        className="w-full font-semibold"
                                    >
                                        Get started
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <img
                            src={'/images/illustrations/easy_send_cta_confetti.svg'}
                            className="z-belowZero absolute right-0 top-12 rotate-180"
                            alt={'Easy send confetti top right'}
                        />
                    </div>
                    <Dialog.Close />
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const LetterEasySendNewLetterDialogTrigger = Dialog.Trigger;
const Root = LetterEasySendNewLetterDialog;

const Trigger = LetterEasySendNewLetterDialogTrigger;

export {
    LetterEasySendNewLetterDialog,
    LetterEasySendNewLetterDialogTrigger,
    //
    Root,
    Trigger,
};
